import React from "react";
import { Row, Col, Form, FormGroup, Label, Input, Card, CardBody } from "reactstrap";
import Select from "react-select";
import { LanguageSettingsValues, SettingsProps } from "../../types/settings";
import SettingsButtons from "./SettingsButtons";
import { Formik } from "formik";
import { languageSettingsValidation } from "rules/settings";

const languageOptions = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'ar', label: 'Arabic' },
  { value: 'zh', label: 'Chinese' },
];

const LanguageSettings: React.FC<SettingsProps> = ({ 
  onGlobalCancel, 
  isSaving, 
  onSave 
}) => {
  const initialValues: LanguageSettingsValues = {
    defaultLanguage: languageOptions[0],
    availableLanguages: [languageOptions[0]],
    autoTranslate: false,
    allowUserLanguage: false,
    fallbackLanguage: languageOptions[0]
  };

  const handleSave = async (values: LanguageSettingsValues) => {
    await onSave(async () => {
      console.log("Form submitted with values:", values);
      await new Promise(resolve => setTimeout(resolve, 1000));
      // TODO: Add actual API call here
    });
  };

  return (
    <div className="bg-transparent">
      <h5 className="mb-4">Language Settings</h5>
      <Formik
        initialValues={initialValues}
        validationSchema={languageSettingsValidation}
        onSubmit={handleSave}
        validateOnMount={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          handleSubmit,
        }) => (
          <Form>
            <Card className="mb-4">
              <CardBody>
                <h6 className="mb-3">Default Language</h6>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="defaultLanguage">System Default Language</Label>
                      <Select
                        id="defaultLanguage"
                        options={languageOptions}
                        value={values.defaultLanguage}
                        onChange={(selected) => setFieldValue("defaultLanguage", selected)}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Available Languages</h6>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="availableLanguages">Enabled Languages</Label>
                      <Select
                        isMulti
                        id="availableLanguages"
                        options={languageOptions}
                        value={values.availableLanguages}
                        onChange={(selected) => setFieldValue("availableLanguages", selected || [])}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Translation Settings</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input 
                        type="checkbox" 
                        id="autoTranslate"
                        name="autoTranslate"
                        onChange={handleChange}
                        checked={values.autoTranslate}
                      />
                      <Label check for="autoTranslate">
                        Enable Auto-Translation
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input 
                        type="checkbox" 
                        id="allowUserLanguage"
                        name="allowUserLanguage"
                        onChange={handleChange}
                        checked={values.allowUserLanguage}
                      />
                      <Label check for="allowUserLanguage">
                        Allow Users to Change Language
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="fallbackLanguage">Fallback Language</Label>
                      <Select
                        id="fallbackLanguage"
                        options={languageOptions}
                        value={values.fallbackLanguage}
                        onChange={(selected) => setFieldValue("fallbackLanguage", selected)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <SettingsButtons
              onSave={handleSubmit}
              onCancel={onGlobalCancel}
              saveDisabled={Object.keys(errors).length > 0}
              isSaving={isSaving}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default LanguageSettings; 